import { useState, useEffect } from 'react';
import { UserEntity } from 'src/store/userApi';
import {
  UserConversationEntity,
  useLazyAdminFetchUserConversationsQuery,
  useAdminDeleteUserConversationMutation,
} from 'src/store/chatApi';
import moment from 'moment';
import { Badge } from 'src/ui/Badge';
import { Button } from 'src/ui/Button';
import { Checkbox } from 'src/ui/Checkbox';
import { EmptyState } from 'src/ui/EmptyState';
import { Modal } from 'src/ui/Modal';
import { Spinner } from 'src/ui/Spinner';
import { useToast } from 'src/ui/Toast';

interface ConversationListProps {
  selectedUser: UserEntity | null;
  selectedConversation: UserConversationEntity | null;
  onSelectConversation: (conversation: UserConversationEntity) => void;
  preSelectConversationId?: string | null;
}

const ConversationList = ({
  selectedUser,
  selectedConversation,
  onSelectConversation,
  preSelectConversationId,
}: ConversationListProps) => {
  const [conversations, setConversations] = useState<UserConversationEntity[]>(
    []
  );
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [conversationToDelete, setConversationToDelete] =
    useState<UserConversationEntity | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedConversations, setSelectedConversations] = useState<
    Set<string>
  >(new Set());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);

  const toast = useToast();
  const [fetchUserConversations] = useLazyAdminFetchUserConversationsQuery();
  const [deleteUserConversation, { isLoading: isDeleting }] =
    useAdminDeleteUserConversationMutation();

  useEffect(() => {
    if (!selectedUser) {
      setConversations([]);
      setSelectedConversations(new Set());
      setIsMultiSelectMode(false);
      return;
    }

    const loadConversations = async () => {
      setLoadingConversations(true);
      try {
        const result = await fetchUserConversations({
          userId: selectedUser.id,
          limit: 100,
        }).unwrap();

        setConversations(result.data);
        setSelectedConversations(new Set());
        setIsMultiSelectMode(false);

        if (preSelectConversationId && result.data) {
          const conversation = result.data.find(
            (conv) => conv.id === preSelectConversationId
          );
          if (
            conversation &&
            (!selectedConversation ||
              selectedConversation.id !== preSelectConversationId)
          ) {
            onSelectConversation(conversation);
          }
        }
      } catch {
        toast({
          type: 'error',
          title: 'Error loading conversations',
          message: 'Failed to fetch user conversations',
        });
      } finally {
        setLoadingConversations(false);
      }
    };

    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, fetchUserConversations]);

  const getTopicName = (topicTargetId: string) =>
    topicTargetId ? `Topic ${topicTargetId}` : 'General';

  const toggleConversationSelection = (conversationId: string) => {
    const next = new Set(selectedConversations);
    if (next.has(conversationId)) {
      next.delete(conversationId);
    } else {
      next.add(conversationId);
    }
    setSelectedConversations(next);
  };

  const selectAllConversations = () =>
    setSelectedConversations(new Set(conversations.map((c) => c.id)));

  const deselectAllConversations = () => setSelectedConversations(new Set());

  const isAllSelected =
    selectedConversations.size === conversations.length &&
    conversations.length > 0;

  const handleDeleteConversation = async () => {
    if (!conversationToDelete) return;
    try {
      await deleteUserConversation(conversationToDelete.id).unwrap();
      setConversations((prev) =>
        prev.filter((conv) => conv.id !== conversationToDelete.id)
      );
      if (selectedConversation?.id === conversationToDelete.id) {
        onSelectConversation(null as any);
      }
      toast({
        type: 'success',
        title: 'Conversation deleted',
        message: 'The conversation has been successfully deleted',
      });
    } catch {
      toast({
        type: 'error',
        title: 'Error deleting conversation',
        message: 'Failed to delete the conversation',
      });
    } finally {
      setConversationToDelete(null);
      setDeleteModalOpen(false);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedConversations.size === 0) return;
    setIsBulkDeleting(true);
    const selectedIds = Array.from(selectedConversations);
    let deletedCount = 0;
    let failedCount = 0;

    for (const conversationId of selectedIds) {
      try {
        await deleteUserConversation(conversationId).unwrap();
        deletedCount++;
        setConversations((prev) =>
          prev.filter((conv) => conv.id !== conversationId)
        );
        setSelectedConversations((prev) => {
          const next = new Set(prev);
          next.delete(conversationId);
          return next;
        });
      } catch {
        failedCount++;
      }
    }

    setSelectedConversations(new Set());
    setIsMultiSelectMode(false);

    if (selectedConversation && selectedIds.includes(selectedConversation.id)) {
      onSelectConversation(null as any);
    }

    if (failedCount === 0) {
      toast({
        type: 'success',
        title: 'Conversations deleted',
        message: `Successfully deleted ${deletedCount} conversation(s)`,
      });
    } else if (deletedCount > 0) {
      toast({
        type: 'warning',
        title: 'Partial success',
        message: `Deleted ${deletedCount}, failed to delete ${failedCount}`,
      });
    } else {
      toast({
        type: 'error',
        title: 'Error deleting conversations',
        message: 'Failed to delete all selected conversations',
      });
    }

    setIsBulkDeleting(false);
    setBulkDeleteModalOpen(false);
  };

  if (!selectedUser) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EmptyState
          icon="messages-square"
          title="No user selected"
          desc="Select a user above to view their conversations."
        />
      </div>
    );
  }

  return (
    <>
      {/* Toolbar row */}
      <div
        className="row"
        style={{
          padding: '10px 14px',
          borderBottom: '1px solid var(--border)',
          gap: 8,
          flexShrink: 0,
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 'var(--fs-sm)', flex: 1 }}>
          Conversations
        </span>

        {isMultiSelectMode ? (
          <div className="row gap-2">
            <Checkbox
              checked={isAllSelected}
              onChange={(checked) => {
                if (checked) {
                  selectAllConversations();
                } else {
                  deselectAllConversations();
                }
              }}
            />
            <span
              className="text-muted"
              style={{ fontSize: 'var(--fs-xs)', minWidth: 24 }}
            >
              All
            </span>
            <Button
              variant="danger"
              size="sm"
              disabled={selectedConversations.size === 0}
              onClick={() => setBulkDeleteModalOpen(true)}
            >
              Delete ({selectedConversations.size})
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsMultiSelectMode(false);
                setSelectedConversations(new Set());
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div className="row gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={conversations.length === 0}
              onClick={() => setIsMultiSelectMode(true)}
            >
              Select
            </Button>
            <Badge color="blue" size="sm">
              {conversations.length}
            </Badge>
          </div>
        )}
      </div>

      {/* List */}
      <div className="grow scroll-y" style={{ padding: 6 }}>
        {loadingConversations ? (
          <div
            style={{ display: 'flex', justifyContent: 'center', padding: 32 }}
          >
            <Spinner />
          </div>
        ) : conversations.length === 0 ? (
          <div style={{ padding: 16 }}>
            <EmptyState
              icon="messages-square"
              title="No conversations"
              desc="This user has no conversations yet."
            />
          </div>
        ) : (
          conversations.map((conversation) => {
            const isSelected = selectedConversation?.id === conversation.id;
            const isChecked = selectedConversations.has(conversation.id);
            const title =
              conversation.title ||
              getTopicName(conversation.conversation.topicTargetId);

            return (
              <div
                key={conversation.id}
                role="button"
                tabIndex={0}
                className={'list-row' + (isSelected ? ' selected' : '')}
                style={{ alignItems: 'flex-start', gap: 8 }}
                onClick={() => {
                  if (isMultiSelectMode) {
                    toggleConversationSelection(conversation.id);
                  } else {
                    onSelectConversation(conversation);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (isMultiSelectMode) {
                      toggleConversationSelection(conversation.id);
                    } else {
                      onSelectConversation(conversation);
                    }
                  }
                }}
              >
                {isMultiSelectMode && (
                  <div
                    style={{ paddingTop: 2 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={isChecked}
                      onChange={() =>
                        toggleConversationSelection(conversation.id)
                      }
                    />
                  </div>
                )}

                <div className="grow" style={{ minWidth: 0 }}>
                  <div
                    className="row"
                    style={{ justifyContent: 'space-between', gap: 6 }}
                  >
                    <span
                      className="truncate"
                      style={{ fontWeight: 500, fontSize: 'var(--fs-sm)' }}
                    >
                      {title}
                    </span>
                    <span
                      className="text-muted"
                      style={{
                        fontSize: 'var(--fs-2xs)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {moment(conversation.updatedAt).format('MMM D')}
                    </span>
                  </div>

                  <div className="row gap-2" style={{ marginTop: 3 }}>
                    {conversation.unread > 0 && (
                      <span
                        style={{
                          minWidth: 18,
                          height: 18,
                          padding: '0 5px',
                          borderRadius: 99,
                          background: 'var(--accent)',
                          color: 'var(--text-on-accent)',
                          fontSize: 10,
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {conversation.unread}
                      </span>
                    )}
                    <span
                      className="text-muted truncate"
                      style={{ fontSize: 'var(--fs-xs)' }}
                    >
                      {conversation.conversation.topic}
                    </span>
                  </div>

                  {conversation.conversation.lastMessage && (
                    <div
                      className="text-muted truncate"
                      style={{
                        fontSize: 'var(--fs-xs)',
                        marginTop: 2,
                        opacity: 0.85,
                      }}
                    >
                      {conversation.conversation.lastMessage.text}
                    </div>
                  )}
                </div>

                {!isMultiSelectMode && (
                  <button
                    className="btn btn-ghost btn-icon btn-sm"
                    style={{ flexShrink: 0, opacity: 0.4 }}
                    aria-label="Delete conversation"
                    title="Delete conversation"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConversationToDelete(conversation);
                      setDeleteModalOpen(true);
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Delete single conversation modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Conversation"
        subtitle="This action cannot be undone."
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button
              variant="secondary"
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={isDeleting}
              onClick={handleDeleteConversation}
            >
              Delete
            </Button>
          </div>
        }
      >
        {conversationToDelete && (
          <div
            style={{
              padding: '10px 14px',
              background: 'var(--bg-muted)',
              borderRadius: 8,
            }}
          >
            <div style={{ fontWeight: 500, fontSize: 'var(--fs-sm)' }}>
              {conversationToDelete.title || 'Untitled Conversation'}
            </div>
            <div
              className="text-muted"
              style={{ fontSize: 'var(--fs-xs)', marginTop: 2 }}
            >
              {moment(conversationToDelete.createdAt).format('LLL')}
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk delete confirmation modal */}
      <Modal
        open={bulkDeleteModalOpen}
        onClose={() => setBulkDeleteModalOpen(false)}
        title="Delete Multiple Conversations"
        subtitle={`${selectedConversations.size} conversation(s) will be permanently deleted.`}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button
              variant="secondary"
              onClick={() => setBulkDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={isBulkDeleting}
              onClick={handleBulkDeleteConfirm}
            >
              Delete {selectedConversations.size} Conversation(s)
            </Button>
          </div>
        }
      >
        <div
          style={{
            maxHeight: 200,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {conversations
            .filter((conv) => selectedConversations.has(conv.id))
            .map((conv) => (
              <div
                key={conv.id}
                style={{
                  padding: '8px 12px',
                  background: 'var(--bg-muted)',
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    fontWeight: 500,
                    fontSize: 'var(--fs-sm)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {conv.title || getTopicName(conv.conversation.topicTargetId)}
                </div>
                <div
                  className="text-muted"
                  style={{ fontSize: 'var(--fs-xs)', marginTop: 2 }}
                >
                  {moment(conv.createdAt).format('LLL')}
                </div>
              </div>
            ))}
        </div>
      </Modal>
    </>
  );
};

export default ConversationList;
